import { ButtonGroup, Colors, Dialog, Intent } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, showError, showSuccess, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { DogDysplasiaApi, DogDysplasiaGridDto, DogDysplasiaSearch } from "../../../../../api";
import { AddButton, CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../../components";
import { useDialog, useGlobalData } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { DogDysplasiaPanel } from "./DogDysplasiaPanel";

export interface IDogDysplasiaListProps {
  dogId: number;
}

export const DogDysplasiaList: React.FunctionComponent<IDogDysplasiaListProps> = ({ dogId }) => {
  const { t } = useTl();
  const api = useApiService(DogDysplasiaApi);
  const [deleteLoading, setdeleteLoading] = React.useState([]);

  const [dogDysplasiaId, setdogDysplasiaId] = React.useState<number>(0);
  const [dialogOpened, setdialogOpened] = React.useState<boolean>(false);
  const { currentLanguage } = useGlobalData();
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [10, 15, 25],
    pageSize: 15,
    sortKeys: { characteristic: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj: DogDysplasiaSearch) => {
      setsobjState(
        DogDysplasiaSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
      return api.baseSearch(
        DogDysplasiaSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { loading, search } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { showDialogPromise } = useDialog();

  const deleteResult = React.useCallback(
    async (dogDysplasiaId: number) => {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage),
        title: t(ETLCodes.Warning),
      });
      if (dialogResult !== "yes") {
        return;
      }
      deleteLoading.push(dogDysplasiaId);
      setdeleteLoading([...deleteLoading]);
      try {
        await api.delete(dogDysplasiaId);
        showSuccess(t(ETLCodes.DeleteSuccess));
        search();
      } catch {
        showError(t(ETLCodes.Error));
      }
      setdeleteLoading(deleteLoading.filter((c) => c !== dogDysplasiaId));
    },
    [api, deleteLoading, search, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "action",
        alignment: "center",
        autoFitContent: true,
        render: (row: DogDysplasiaGridDto) => (
          <ButtonGroup>
            <EditButton
              minimal={true}
              onClick={() => {
                setdogDysplasiaId(row.dogDysplasiaId);
                setdialogOpened(true);
              }}
            />
            <DeleteButton
              onDelete={() => {
                deleteResult(row.dogDysplasiaId);
              }}
              loading={deleteLoading.find((c) => c === row.dogDysplasiaId) > 0}
            />
          </ButtonGroup>
        ),
      },
      {
        header: () => t(ETLCodes.Characteristic),
        fieldName: "characteristic",
        render: (row: DogDysplasiaGridDto) => (
          <Link target="_blank" to={`${ERoutes.characteristics}/${row.characteristicId}/detail/view`}>
            {row.characteristic}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Value),
        fieldName: "healthValue",
        render: (row: DogDysplasiaGridDto) => (
          <div
            style={
              tableState.data
                .filter((e) => e.characteristicId === row.characteristicId && e !== row)
                .some((e) => row.createdOn > e.createdOn) ||
              tableState.data.filter((e) => e.characteristicId === row.characteristicId && e !== row).length === 0
                ? { fontWeight: "bold" }
                : {}
            }
          >
            {row.value}
          </div>
        ),
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: DogDysplasiaGridDto) => (
          <Link target="_blank" to={`${ERoutes.dysplasiaRequest}/${row.dysplasiaRequestId}/detail/view`}>
            {row.requestNumber}
          </Link>
        ),
      },
    ],
    [deleteLoading, deleteResult, t, tableState.data]
  );

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Dysplasia)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            <AddButton
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
              onClick={() => {
                setdogDysplasiaId(0);
                setdialogOpened(true);
              }}
            />
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.Characteristics)}
        isOpen={dialogOpened}
        onClose={() => setdialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 850, height: 290, background: Colors.WHITE }}
      >
        <div style={{ marginRight: "2rem" }}>
          <DogDysplasiaPanel
            dogId={dogId}
            dogDysplasiaId={dogDysplasiaId}
            postSave={() => {
              setdialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
